'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@crabs-ai/ds-react';
import {
  AnimationDragData,
  AnimationType,
  ANIMATION_DRAG_TYPE,
  setCurrentAnimationDragData,
} from './animation-drag-data';
import AnimationPreview from './AnimationPreview';

interface AnimationItemProps {
  type: AnimationType;
  name: string;
  /** 지정 시 클릭으로도 추가할 수 있다(Inspector 의 "애니메이션 추가" 팝오버 용). */
  onSelect?: (type: AnimationType) => void;
}

const PREVIEW_DURATION = 30; // 프리뷰 프레임 수
const FRAME_RATE = 30; // fps

export default function AnimationItem({
  type,
  name,
  onSelect,
}: AnimationItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // hover 시 애니메이션 시작
  useEffect(() => {
    if (isHovered && !isDragging) {
      startTimeRef.current = null;

      const animate = (timestamp: number) => {
        if (!startTimeRef.current) {
          startTimeRef.current = timestamp;
        }

        const elapsed = timestamp - startTimeRef.current;
        const duration = (PREVIEW_DURATION / FRAME_RATE) * 1000; // ms
        const currentProgress = Math.min(elapsed / duration, 1);

        setProgress(currentProgress);

        // 애니메이션이 끝나면 멈춤 (반복하지 않음)
        if (currentProgress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        }
      };

      animationRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    } else {
      // hover 종료 시 초기 상태로 리셋
      setProgress(0);
      startTimeRef.current = null;
    }
  }, [isHovered, isDragging]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // 드래그 시작 핸들러
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      setCurrentAnimationDragData(null);

      const dragData: AnimationDragData = {
        type,
        name,
      };

      e.dataTransfer.setData(ANIMATION_DRAG_TYPE, JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = 'copy';
      setIsDragging(true);

      // 전역 드래그 데이터 설정 (dragover에서 사용)
      setCurrentAnimationDragData(dragData);

      // 투명한 드래그 이미지 설정 (커스텀 오버레이 사용)
      const transparentImage = document.createElement('div');
      transparentImage.style.cssText = `
        position: absolute;
        top: -9999px;
        left: -9999px;
        width: 1px;
        height: 1px;
        opacity: 0;
      `;
      document.body.appendChild(transparentImage);
      e.dataTransfer.setDragImage(transparentImage, 0, 0);

      // 드래그 이미지 설정 후 DOM에서 제거 (약간의 딜레이 필요)
      requestAnimationFrame(() => {
        document.body.removeChild(transparentImage);
      });
    },
    [type, name],
  );

  // 드래그 종료 핸들러
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setCurrentAnimationDragData(null);
  }, []);

  return (
    <div
      // 클릭 추가 모드(Inspector 팝오버)에서는 드래그를 끈다 — 팝오버 안에서 네이티브 드래그가
      // 시작되면 outside-pointer 감지와 얽혀 닫힘·포커스가 꼬인다.
      draggable={!onSelect}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...(onSelect
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: () => onSelect(type),
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              onSelect(type);
            },
          }
        : {})}
      className={cn(
        'flex flex-col p-4xs',
        onSelect ? 'cursor-pointer' : 'cursor-grab',
        isDragging && 'opacity-50',
      )}
    >
      <div
        className={cn(
          'aspect-[16/9] w-full overflow-hidden rounded-xs transition-all',
          isHovered
            ? 'border-sys-style-primary-default'
            : 'border-sys-style-neutral-default',
        )}
      >
        <AnimationPreview
          type={type}
          progress={progress}
        />
      </div>
      <span className="p-2xs text-title-small text-sys-cont-neutral-default">
        {name}
      </span>
    </div>
  );
}
